import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Skeleton } from '@mui/material'
import { useTakeScreenshot } from 'commons/customHooks'
import { formatDate } from 'commons/utils/date'
import { numberToDecimalString as toDecimal } from 'commons/utils/number'
import {
  Accordion,
  ActionTabs,
  Icon,
  type ItemActionTabsType,
  LoadingHavaianas,
  Radio,
  SelectMenu,
  SlimButton,
  ToggleButton,
  Typography,
} from 'components'
import { Offices } from 'constants/enums'
import type { OfficeGroup } from 'services/home/interfaces'
import {
  getGeneralMeta,
  getGeneralMetaReport,
  getMetaOptions,
  getRanking,
  getRankingReport,
  resetRankingPagination,
  setRankingQuota,
} from 'store/HomeContext/homeSlice'
import { useDispatch, useSelector } from 'store/store'

import GeneralTable from '../GeneralTable'
import RankingTable from '../RankingTable'
import { officeColors } from './constants'
import * as S from './styles'

export type OfficeTypes = 'TODOS' | Offices.AC60 | Offices.AESP | Offices.ASMD
export type GoalViews = 'Ranking' | 'General'

export const GoalProgress = () => {
  const dispatch = useDispatch()
  const {
    loadingRanking: loading,
    rankingData,
    generalMetaData,
    rankingTotalVolume,
    rankingQuotaOptions,
    rankingOffices,
    rankingQuota,
    officeGroups,
    rankingUpdateDate,
    loadingRankingReport,
  } = useSelector((state) => state.home)
  const [selectedOffice, setSelectedOffice] = useState<OfficeTypes>('TODOS')
  const [selectedView, setSelectedView] = useState<GoalViews>('Ranking')
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
  const [currentTab, setCurrentTab] = useState('0')
  const [isPrinting, setIsPrinting] = useState(false)

  const screenshotComponentRef = useRef(null)
  const takeScreenshot = useTakeScreenshot()

  const handleTakeScreenshot = useCallback(() => {
    setIsPrinting(true)
    setTimeout(() => {
      takeScreenshot(screenshotComponentRef.current)
      setIsPrinting(false)
    }, 500)
  }, [])

  const handleRankingReport = () => {
    if (selectedView === 'Ranking') {
      dispatch(
        getRankingReport({
          AnoMesCota: quota,
          Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
          Gerente: null,
          Regional: selectedGroup,
        }),
      )
    }
    if (selectedView === 'General') {
      dispatch(
        getGeneralMetaReport({
          AnoMesCota: quota,
          Equipe: null,
          Escritorio: selectedOffice,
        }),
      )
    }
  }

  const quota = useMemo(() => formatDate(rankingQuota, 'YYYYMM', 'MM/YY'), [rankingQuota])

  const disableReport = useMemo(() => {
    const emptyRanking = selectedView === 'Ranking' && !rankingData.length
    const emptyGeneral = selectedView === 'General' && !generalMetaData.length
    return loading || loadingRankingReport || emptyRanking || emptyGeneral
  }, [loading, loadingRankingReport, rankingData, generalMetaData, selectedView])

  const rankingOfficesTitles: Record<OfficeTypes, string> = {
    TODOS: 'Todos',
    [Offices.AC60]: 'Canal Alimentar (farma e outros)',
    [Offices.AESP]: 'Canal Especializado',
    [Offices.ASMD]: 'AS Moderno',
  }

  const getOfficeTitle = () => {
    return (
      <S.Row>
        <S.OfficeTitle design="hav-default-02-text">Escritório {selectedOffice}</S.OfficeTitle>
        <S.TitleDivider />
        {selectedOffice !== 'TODOS' && (
          <S.OfficeTitle
            color={
              [Offices.AC60, Offices.AESP, Offices.ASMD].includes(selectedOffice)
                ? officeColors[selectedOffice]
                : officeColors['TODOS']
            }
            design="hav-default-02-text"
          >
            {rankingOfficesTitles[selectedOffice]}
          </S.OfficeTitle>
        )}
      </S.Row>
    )
  }

  const getUpdateDate = () => {
    const day = formatDate(rankingUpdateDate, 'DD/MM', 'DD/MM/YYYY HH:mm:ss')
    const time = formatDate(rankingUpdateDate, 'HH[h]mm', 'DD/MM/YYYY HH:mm:ss')
    return `Dados atualizados em ${day} às ${time}`
  }

  const handleOfficeChange = (office: OfficeTypes) => {
    setSelectedOffice(office)
    dispatch(resetRankingPagination())
    setSelectedGroup(null)
    setCurrentTab('0')
    if (selectedView === 'Ranking') {
      dispatch(
        getRanking(
          {
            AnoMesCota: quota,
            Escritorio: office === 'TODOS' ? null : office,
            Gerente: null,
            Regional: null,
          },
          { linhas: 10, paginas: 0 },
        ),
      )
    } else {
      dispatch(
        getGeneralMeta({
          AnoMesCota: quota,
          Equipe: null,
          Escritorio: office,
        }),
      )
    }
  }

  const handleTabTitle = (title: string) => {
    if (selectedOffice === Offices.AESP) return title.replace('HAVA ', '')
    return title.replace('Havaianas ', '')
  }

  const getTabItems = () => {
    const items: ItemActionTabsType[] = [
      {
        label: 'Todas',
        onClick: () => handleTabChange(0),
      },
    ]
    officeGroups.forEach((group, index) => {
      items.push({
        label: handleTabTitle(group.Descricao),
        onClick: () => handleTabChange(index + 1, group),
      })
    })
    return items
  }

  const handleTabChange = (index: number, group?: OfficeGroup) => {
    setCurrentTab(`${index}`)
    dispatch(resetRankingPagination())
    setSelectedGroup(group ? group.Codigo : null)
    dispatch(
      getRanking(
        {
          AnoMesCota: quota,
          Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
          Gerente: null,
          Regional: group ? group.Codigo : null,
        },
        { linhas: 10, paginas: 0 },
      ),
    )
  }

  const handleViewChange = (view: GoalViews) => {
    if (view === 'Ranking') {
      dispatch(resetRankingPagination())
      setSelectedGroup(null)
      setCurrentTab('0')
      dispatch(
        getRanking(
          {
            AnoMesCota: quota,
            Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
            Gerente: null,
            Regional: null,
          },
          { linhas: 10, paginas: 0 },
        ),
      )
    }
    if (view === 'General') {
      const office =
        selectedOffice === 'TODOS'
          ? (rankingOffices[0].cd_escr_vend as OfficeTypes)
          : selectedOffice
      setSelectedOffice(office)
      dispatch(
        getGeneralMeta({
          AnoMesCota: quota,
          Equipe: null,
          Escritorio: office,
        }),
      )
    }
    setSelectedView(view)
  }

  const handleQuotaSelect = (value: string) => {
    dispatch(resetRankingPagination())
    dispatch(setRankingQuota(value))
    if (selectedView === 'Ranking') {
      dispatch(
        getRanking(
          {
            AnoMesCota: formatDate(value, 'YYYYMM', 'MM/YY'),
            Escritorio: selectedOffice === 'TODOS' ? null : selectedOffice,
            Gerente: null,
            Regional: selectedGroup,
          },
          { linhas: 10, paginas: 0 },
        ),
      )
    }
    if (selectedView === 'General') {
      dispatch(
        getGeneralMeta({
          AnoMesCota: formatDate(value, 'YYYYMM', 'MM/YY'),
          Equipe: null,
          Escritorio: selectedOffice,
        }),
      )
    }
  }

  useEffect(() => {
    dispatch(getMetaOptions())
  }, [])

  return (
    <S.Wrapper>
      <Accordion
        summaryContent={
          <Typography design="hav-h5-text">Progresso de metas: Equipes de vendas</Typography>
        }
      >
        <S.Content>
          <S.Row>
            {selectedView === 'Ranking' && (
              <ToggleButton
                active={selectedOffice === 'TODOS'}
                content="Todos"
                disabled={loading || loadingRankingReport}
                onClick={() => handleOfficeChange('TODOS')}
              />
            )}
            {rankingOffices.map((office) => (
              <ToggleButton
                active={selectedOffice === office.cd_escr_vend}
                content={`Escritório ${office.cd_escr_vend}`}
                disabled={loading || loadingRankingReport}
                key={office.cd_escr_vend}
                onClick={() => handleOfficeChange(office.cd_escr_vend as OfficeTypes)}
              />
            ))}
          </S.Row>
          <S.SectionCard>
            <S.TitleRow>
              <S.Row>
                {getOfficeTitle()}
                <S.OfficeTooltip>
                  {loading ? (
                    <Skeleton height={17} width={200} />
                  ) : (
                    <Typography className="office-info" design="hav-h8-text">
                      {getUpdateDate()}
                    </Typography>
                  )}
                </S.OfficeTooltip>
              </S.Row>
              <S.ReportRow>
                <SlimButton
                  content="Relatório"
                  disabled={disableReport}
                  icon={<Icon icon="download" size="15" />}
                  loading={loadingRankingReport}
                  onClick={handleRankingReport}
                  variant={'primary'}
                />
                <SlimButton
                  content="Imagem"
                  disabled={disableReport}
                  icon={<Icon icon="download" size="15" />}
                  onClick={handleTakeScreenshot}
                  variant={'primary'}
                />
              </S.ReportRow>
            </S.TitleRow>
            <S.RowDivider />
            <S.TitleRow>
              <S.RadioGroup>
                <SelectMenu
                  disabled={loading || loadingRankingReport}
                  hasBorder
                  label="Mês cota"
                  onChange={handleQuotaSelect}
                  options={rankingQuotaOptions}
                  value={rankingQuota}
                />
                <Radio
                  checked={selectedView === 'Ranking'}
                  disabled={loading || loadingRankingReport}
                  label="Ranking"
                  name="goal-view"
                  onChange={() => handleViewChange('Ranking')}
                  value="ranking"
                />
                <Radio
                  checked={selectedView === 'General'}
                  disabled={loading || loadingRankingReport}
                  label="Geral"
                  name="goal-view"
                  onChange={() => handleViewChange('General')}
                  value="general"
                />
              </S.RadioGroup>
              <S.MetaWrap>
                <S.MetaLabel design="hav-h7-text">Meta total</S.MetaLabel>
                {loading ? (
                  <Skeleton height={21} width={50} />
                ) : (
                  <S.MetaValue design="hav-h6-text">{toDecimal(rankingTotalVolume)}</S.MetaValue>
                )}
              </S.MetaWrap>
            </S.TitleRow>
          </S.SectionCard>
          {loading ? (
            <LoadingHavaianas />
          ) : (
            <>
              {selectedOffice !== 'TODOS' && selectedView === 'Ranking' && (
                <S.TabsWrapper>
                  <ActionTabs
                    currentItem={currentTab}
                    disabled={loading || loadingRankingReport}
                    items={getTabItems()}
                  />
                </S.TabsWrapper>
              )}
              {selectedView === 'Ranking' && (
                <RankingTable
                  quota={quota}
                  screenshotComponentRef={screenshotComponentRef}
                  selectedGroup={selectedGroup}
                  selectedOffice={selectedOffice}
                />
              )}
              {selectedView === 'General' && (
                <GeneralTable
                  isPrinting={isPrinting}
                  quota={quota}
                  screenshotComponentRef={screenshotComponentRef}
                  selectedOffice={selectedOffice}
                />
              )}
            </>
          )}
        </S.Content>
      </Accordion>
    </S.Wrapper>
  )
}
